import React, { Suspense, useState } from 'react'
import { graphql } from 'relay-runtime'
import { useMutation } from 'react-relay'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
} from '@mui/material'
import { toast } from 'react-toastify'
import { FeatureFlagSelector } from '../FeatureFlagSelector'
import { cancelButtonSx } from '../styles'
import { metaAdAccountIdError, requiredError } from '../validation'
import {
  BrandAdvancedFields,
  BrandAdvancedValues,
  CurrencyValue,
  FbTokenValue,
  GranularityValue,
  emptyBrandAdvanced,
  toBrandConfigInput,
} from './BrandAdvancedFields'
import { BrandFormCreateMutation } from './__generated__/BrandFormCreateMutation.graphql'
import { BrandFormUpdateMutation } from './__generated__/BrandFormUpdateMutation.graphql'

export interface AdminBrandInput {
  id: string
  name: string
  metaAdAccountExternalId: string | null
  featureFlagIds: string[]
  currencyType: string | null
  fbAccessTokenType: string | null
  dataGranularity: string | null
  minAdSpend: number | null
  insightsThreshold: number | null
}

const createMutation = graphql`
  mutation BrandFormCreateMutation($input: CreateBrandRequest!) {
    createBrand(input: $input) {
      id
    }
  }
`

const updateMutation = graphql`
  mutation BrandFormUpdateMutation($input: UpdateBrandRequest!) {
    updateBrand(input: $input) {
      id
    }
  }
`

const advancedFromBrand = (brand?: AdminBrandInput): BrandAdvancedValues =>
  brand
    ? {
        currencyType: (brand.currencyType ?? '') as CurrencyValue,
        fbAccessTokenType: (brand.fbAccessTokenType ?? '') as FbTokenValue,
        dataGranularity: (brand.dataGranularity ?? '') as GranularityValue,
        minAdSpend: brand.minAdSpend != null ? String(brand.minAdSpend) : '',
        insightsThreshold:
          brand.insightsThreshold != null
            ? String(brand.insightsThreshold)
            : '',
      }
    : emptyBrandAdvanced()

interface BrandFormProps {
  accountId: string
  // When provided, the form edits this brand; otherwise it creates a new one.
  brand?: AdminBrandInput
  // Account-level flags, hidden from the brand flag selector.
  excludeIds: string[]
  onSaved: () => void
  onCancel: () => void
}

/**
 * Inline brand create/edit form (name + feature flags + advanced settings).
 * Rendered inside the account detail panel — no dialogs.
 */
export const BrandForm = ({
  accountId,
  brand,
  excludeIds,
  onSaved,
  onCancel,
}: BrandFormProps) => {
  const isEdit = brand != null
  const [name, setName] = useState(brand?.name ?? '')
  const [metaAdAccountId, setMetaAdAccountId] = useState(
    brand?.metaAdAccountExternalId ?? ''
  )
  const [featureFlagIds, setFeatureFlagIds] = useState<string[]>(
    brand?.featureFlagIds ?? []
  )
  const [advanced, setAdvanced] = useState<BrandAdvancedValues>(
    advancedFromBrand(brand)
  )
  const updateAdvanced = (patch: Partial<BrandAdvancedValues>) =>
    setAdvanced((prev) => ({ ...prev, ...patch }))

  const [submitted, setSubmitted] = useState(false)
  const nameErr = requiredError(name, 'Brand name')
  const metaErr = metaAdAccountIdError(metaAdAccountId)

  const [createBrand, creating] =
    useMutation<BrandFormCreateMutation>(createMutation)
  const [updateBrand, updating] =
    useMutation<BrandFormUpdateMutation>(updateMutation)
  const saving = creating || updating

  // Drop flags already covered at the account level.
  const effectiveFlagIds = featureFlagIds.filter(
    (id) => !excludeIds.includes(id)
  )

  const handleSave = () => {
    setSubmitted(true)
    if (nameErr || metaErr) return
    const onError = (error: Error) => {
      console.error(error)
      toast.error(error.message || 'Unable to save brand.')
    }
    if (isEdit) {
      updateBrand({
        variables: {
          input: {
            id: brand!.id,
            name,
            metaAdAccountExternalId: metaAdAccountId.trim(),
            featureFlagIds: effectiveFlagIds,
            ...toBrandConfigInput(advanced),
          },
        },
        onCompleted: () => {
          toast.success('Brand updated.')
          onSaved()
        },
        onError,
      })
    } else {
      createBrand({
        variables: {
          input: {
            accountId,
            name,
            metaAdAccountExternalId: metaAdAccountId.trim(),
            featureFlagIds: effectiveFlagIds,
            ...toBrandConfigInput(advanced),
          },
        },
        onCompleted: () => {
          toast.success('Brand created.')
          onSaved()
        },
        onError,
      })
    }
  }

  return (
    <div>
      <TextField
        autoFocus
        fullWidth
        margin="normal"
        label="Brand Name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
        error={submitted && !!nameErr}
        helperText={submitted ? nameErr ?? undefined : undefined}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Meta Ad Account ID"
        value={metaAdAccountId}
        onChange={(event) => setMetaAdAccountId(event.target.value)}
        required
        error={submitted && !!metaErr}
        helperText={
          submitted && metaErr
            ? metaErr
            : 'Format: act_<id>. For agency accounts, use the brand’s own ad account ID.'
        }
      />

      <BrandAdvancedFields values={advanced} onChange={updateAdvanced} />

      <Divider sx={{ my: 1.5 }} />

      <Suspense
        fallback={
          <div className="py-2">
            <CircularProgress size={20} />
          </div>
        }
      >
        <FeatureFlagSelector
          value={featureFlagIds}
          excludeIds={excludeIds}
          onChange={setFeatureFlagIds}
          scope="brand"
        />
      </Suspense>

      <Divider sx={{ my: 1.5 }} />
      <div className="flex justify-end gap-2">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
        <Button
          variant="contained"
          sx={cancelButtonSx}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}

interface BrandFormDialogProps {
  accountId: string
  accountName: string
  brand?: AdminBrandInput
  excludeIds: string[]
  onSaved: () => void
  onClose: () => void
}

/** The brand create/edit form in a dialog. */
export const BrandFormDialog = ({
  accountId,
  accountName,
  brand,
  excludeIds,
  onSaved,
  onClose,
}: BrandFormDialogProps) => (
  <Dialog
    open
    onClose={onClose}
    fullWidth
    PaperProps={{ sx: { width: '100%', maxWidth: 560 } }}
  >
    <DialogTitle sx={{ color: 'primary.main' }}>
      {brand ? 'Edit Brand' : 'Add Brand'} — {accountName}
    </DialogTitle>
    <DialogContent>
      <BrandForm
        accountId={accountId}
        brand={brand}
        excludeIds={excludeIds}
        onSaved={onSaved}
        onCancel={onClose}
      />
    </DialogContent>
  </Dialog>
)

export default BrandForm
